/**
 * 대시보드 패널들이 공유하는 작은 프리미티브 (상태 점·아이콘·레벨 배지·버튼·칩).
 */
import { Activity, AlertTriangle, Box, CheckCircle2, Cog, Database, Factory, Monitor, PowerOff, RefreshCw, XCircle, } from "lucide-react";
import { NODE_TYPE_LABEL_KO, STATUS_LABEL_KO, statusColorVar } from "../../lib/format";
export function cn(...parts) {
    return parts.filter(Boolean).join(" ");
}
export const STATUS_ICON = {
    healthy: CheckCircle2,
    running: Activity,
    warning: AlertTriangle,
    error: XCircle,
    recovering: RefreshCw,
    offline: PowerOff,
};
export const NODE_TYPE_ICON = {
    source: Factory,
    processor: Cog,
    model: Box,
    storage: Database,
    output: Monitor,
};
export const LEVEL_LABEL_KO = {
    info: "정보",
    success: "성공",
    warning: "경고",
    error: "오류",
};
export const LEVEL_SHORT = {
    info: "INFO",
    success: "SUCCESS",
    warning: "WARNING",
    error: "ERROR",
};
export const LEVEL_COLOR_VAR = {
    info: "var(--color-level-info)",
    success: "var(--color-level-success)",
    warning: "var(--color-level-warning)",
    error: "var(--color-level-error)",
};
export function StatusDot({ status, className, pulse }) {
    return (<span aria-hidden className={cn("inline-block size-2 shrink-0 rounded-full", pulse && status === "error" && "status-pulse", className)} style={{ background: statusColorVar(status) }}/>);
}
export function StatusIcon({ status, className, size = 14 }) {
    const Icon = STATUS_ICON[status];
    return (<Icon aria-hidden size={size} className={cn("shrink-0", status === "recovering" && "animate-spin [animation-duration:2.5s]", className)} style={{ color: statusColorVar(status) }}/>);
}
export function StatusBadge({ status, className }) {
    const color = statusColorVar(status);
    return (<span className={cn("inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium leading-none", className)} style={{
            color,
            borderColor: `color-mix(in srgb, ${color} 45%, transparent)`,
            background: `color-mix(in srgb, ${color} 12%, transparent)`,
        }} title={STATUS_LABEL_KO[status]}>
      <StatusIcon status={status} size={12}/>
      {STATUS_LABEL_KO[status]}
    </span>);
}
export function NodeTypeIcon({ nodeType, className, size = 14, }) {
    const Icon = NODE_TYPE_ICON[nodeType];
    return <Icon aria-hidden size={size} className={cn("shrink-0 text-fg-muted", className)}/>;
}
export function nodeTypeLabel(t) {
    return NODE_TYPE_LABEL_KO[t];
}
export function LevelBadge({ level }) {
    const color = LEVEL_COLOR_VAR[level];
    return (<span className="inline-block w-[66px] shrink-0 rounded px-1 py-px text-center text-[10px] font-semibold tracking-wide" style={{ color, background: `color-mix(in srgb, ${color} 15%, transparent)` }} title={LEVEL_LABEL_KO[level]}>
      {LEVEL_SHORT[level]}
    </span>);
}
export function IconButton({ label, onClick, children, active, className, disabled, style, }) {
    return (<button type="button" aria-label={label} title={label} aria-pressed={active} disabled={disabled} onClick={onClick} style={style} className={cn("inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-border text-fg-muted transition-colors", "hover:bg-surface-hover hover:text-fg focus-visible:outline-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:opacity-40", active && "border-border-strong bg-accent-soft text-fg", className)}>
      {children}
    </button>);
}
export function Chip({ label, active, onClick, color, count, title, }) {
    return (<button type="button" aria-pressed={active} title={title ?? label} onClick={onClick} className={cn("inline-flex h-6 max-w-full items-center gap-1 rounded-full border px-2 text-[11px] leading-none transition-colors", active
            ? "border-border-strong bg-accent-soft text-fg"
            : "border-border text-fg-muted hover:bg-surface-hover hover:text-fg")}>
      {color && <span aria-hidden className="size-1.5 shrink-0 rounded-full" style={{ background: color }}/>}
      <span className="truncate">{label}</span>
      {count != null && <span className="tabular text-fg-subtle">{count}</span>}
    </button>);
}
export function SectionTitle({ children, right }) {
    return (<div className="flex items-center justify-between px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">
      <span className="truncate">{children}</span>
      {right}
    </div>);
}
export function Skeleton({ className }) {
    return <div aria-hidden className={cn("skeleton", className)}/>;
}
